import './carrier-compliance-section.css';

type Accent = 'blue' | 'red' | 'green';
type ComplianceCardProps = {
  accent: Accent;
  className?: string;
  items: readonly string[];
  title: string;
};

const violationScore = [
  'Unsafe Driving <65% CSA',
  'Hours of Service Compliance <65% CSA',
  'Crash Indicator <65% CSA',
  'Vehicle Maintenance <80% CSA',
  'Driver Fitness <80% CSA',
] as const;

const automaticFail = [
  'If Unsafe Driving is above 80% CSA',
  'If ANY Controlled Substances Issues',
  'If 2 or more categories are above the threshold',
  'If DRIVER Fitness has a history of being cited for not speaking English',
] as const;

const insuranceRules = [
  'AM Best Rating of B+ or above for both Auto & Cargo',
  'RISK RETENTION GROUP INSURANCE (RRG) is NOT allowed',
  'Canal Insurance on Cargo is NOT allowed',
  'Geico Insurance is NOT allowed',
  'Progressive Insurance & its subsidiaries are NOT allowed for all temp control loads',
] as const;

// Shared vector geometry keeps the reference's filled silhouettes consistent.
function ShieldSymbol({ mark }: { mark: 'check' | 'warning' | 'star' }) {
  return (
    <svg viewBox="0 0 32 36" fill="none" focusable="false">
      <path
        d="M16 1 29 6a3 3 0 0 1 2 3v10c0 8-6 13-15 16C7 32 1 27 1 19V9a3 3 0 0 1 2-3L16 1Z"
        fill="currentColor"
      />
      {mark === 'check' ? (
        <path
          d="m11 18 3.5 3.5L22 13"
          stroke="#071b31"
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : mark === 'warning' ? (
        <>
          <path
            d="M16 11v8"
            stroke="#071b31"
            strokeWidth="2.8"
            strokeLinecap="round"
          />
          <circle cx="16" cy="24" r="1.6" fill="#071b31" />
        </>
      ) : (
        <path
          d="m16 9 2.8 5.5 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L7 15.4l6.2-.9L16 9Z"
          fill="#071b31"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function ShieldIcon({ accent }: { accent: Accent }) {
  return (
    <span
      className={`carrier-compliance__shield carrier-compliance__shield--${accent}`}
      aria-hidden="true"
    >
      <ShieldSymbol mark={accent === 'red' ? 'warning' : 'check'} />
    </span>
  );
}

function CheckItem({
  accent,
  children,
  className = '',
}: {
  accent: Accent;
  children: string;
  className?: string;
}) {
  return (
    <li className={className}>
      <span
        className={`carrier-compliance__check carrier-compliance__check--${accent}`}
        aria-hidden="true"
      >
        <svg viewBox="0 0 12 12" fill="none" focusable="false">
          <path
            d="m3 6 2 2 4-4.5"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span>{children}</span>
    </li>
  );
}

function ComplianceCard({
  accent,
  className = '',
  items,
  title,
}: ComplianceCardProps) {
  return (
    <article className={`carrier-compliance__card ${className}`.trim()}>
      <div className="carrier-compliance__card-heading">
        <ShieldIcon accent={accent} />
        <h3>{title}</h3>
      </div>
      <ul>
        {items.map((item) => (
          <CheckItem accent={accent} key={item}>
            {item}
          </CheckItem>
        ))}
      </ul>
    </article>
  );
}

function UtilityIcon({ icon }: { icon: 'gauge' | 'document' | 'verified' }) {
  return (
    <span className="carrier-compliance__utility-icon" aria-hidden="true">
      {icon === 'verified' ? (
        <ShieldSymbol mark="star" />
      ) : icon === 'gauge' ? (
        <svg viewBox="0 0 36 36" fill="none" focusable="false">
          <circle cx="18" cy="18" r="17" fill="currentColor" />
          <path
            d="M18 2v3m11-1-2 3m7 5-3 1m3 10-3-1m-3 10-2-3M8 32l2-3M2 23l3-1M2 12l3 1M7 5l2 3"
            stroke="#071b31"
            strokeWidth="2.3"
            strokeLinecap="round"
          />
          <path
            d="M18 11c-1 0-5 7-5 10a5 5 0 0 0 10 0c0-3-4-10-5-10Z"
            fill="#071b31"
          />
        </svg>
      ) : (
        <svg viewBox="0 0 36 36" fill="none" focusable="false">
          <rect x="1" y="1" width="34" height="27" rx="6" fill="currentColor" />
          <path
            d="M13 7h10M8 13h20"
            stroke="#071b31"
            strokeWidth="2.3"
            strokeLinecap="round"
          />
          <path
            d="m12 26-4 7 5-1 3 3 2-7 2 7 3-3 5 1-4-7"
            fill="currentColor"
            stroke="#071b31"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <circle
            cx="18"
            cy="24"
            r="7"
            fill="currentColor"
            stroke="#071b31"
            strokeWidth="2.3"
          />
        </svg>
      )}
    </span>
  );
}

function UtilityCard({
  compact = false,
  icon,
  title,
  items,
}: {
  compact?: boolean;
  icon: 'gauge' | 'document' | 'verified';
  title: string;
  items: readonly string[];
}) {
  return (
    <article
      className={`carrier-compliance__card carrier-compliance__utility-card ${compact ? '!block !px-[18px] !py-[22px]' : ''}`.trim()}
    >
      <div
        className={`carrier-compliance__card-heading ${compact ? '!mb-3 !gap-2 !border-r-0 !pr-0' : ''}`.trim()}
      >
        <UtilityIcon icon={icon} />
        <h3
          className={
            compact
              ? 'min-[1200px]:!whitespace-nowrap min-[1200px]:!text-[clamp(19px,1.45vw,22px)]'
              : undefined
          }
        >
          {title}
        </h3>
      </div>
      <ul>
        {items.map((item) => (
          <CheckItem
            accent="blue"
            className={
              compact
                ? 'min-[1200px]:!grid-cols-[17px_minmax(0,1fr)] min-[1200px]:!gap-2 min-[1200px]:!whitespace-nowrap min-[1200px]:!text-[clamp(16px,1.15vw,17px)]'
                : undefined
            }
            key={item}
          >
            {item}
          </CheckItem>
        ))}
      </ul>
    </article>
  );
}

export function CarrierComplianceSection() {
  return (
    <section
      className="carrier-compliance"
      aria-labelledby="carrier-compliance-title"
    >
      <div className="carrier-compliance__inner ship-smart-content-rail container">
        <p className="carrier-compliance__eyebrow">
          <span aria-hidden="true" />
          Qualification standards for safe, compliant, and insurable carrier
          capacity.
        </p>
        <h2 id="carrier-compliance-title">Carrier compliance criteria</h2>

        <div className="carrier-compliance__grid">
          <div className="carrier-compliance__column carrier-compliance__column--left">
            <ComplianceCard
              accent="blue"
              className="carrier-compliance__card--violation"
              items={violationScore}
              title="Violation score"
            />
            <UtilityCard
              icon="gauge"
              items={['Driver OOS % <10%', 'Vehicle OOS % <30%']}
              title="OOS%"
            />
            <div className="carrier-compliance__standards-grid grid grid-cols-2 gap-[16px] max-[767.98px]:grid-cols-1">
              <UtilityCard
                compact
                icon="document"
                items={['Minimum 60 points']}
                title="Bluewire"
              />
              <UtilityCard
                compact
                icon="verified"
                items={['Minimum D- (60 points)']}
                title="Carrier Assure"
              />
            </div>
          </div>

          <div className="carrier-compliance__column carrier-compliance__column--right">
            <ComplianceCard
              accent="red"
              items={automaticFail}
              title="Automatic fail"
            />
            <ComplianceCard
              accent="green"
              className="carrier-compliance__card--insurance"
              items={insuranceRules}
              title="Insurance rules"
            />
            <p className="carrier-compliance__note">
              Includes Artisan and Trucks Casualty, United Financial Casualty,
              Drive New Jersey, Blue Hill Specialty Insurance Company.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
